import React, { useState } from 'react';
import { useEffect } from 'react';
import API from '../api';
import { useNavigate } from 'react-router-dom';
import './login.css';
import logo from '../assets/merchant-marvels.jpg';

function Register() {
  const [form, setForm] = useState({
    name: '',
    bname: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    phone: '',
    role: 'admin',
  });

  const [step, setStep] = useState(1);
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [generatedEmailCode, setGeneratedEmailCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Wait for Google Places script to load
    if (!window.google) return;

    const input = document.getElementsByName("address")[0];
    if (!input) return;

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place?.formatted_address) {
        setForm(prev => ({ ...prev, address: place.formatted_address }));
      }
    });
  }, []);


  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'email' ? value.toLowerCase() : value,
    }));
    setError('');
  };

  const generateEmailCode = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

  const sendEmailVerification = async () => {
    setError('');
    const requiredFields = [
      'name',
      'bname',
      'email',
      'password',
      'confirmPassword',
      'address',
      'phone',
    ];
    const emptyFields = requiredFields.filter((field) => !form[field]?.trim());

    if (emptyFields.length > 0) {
      setError('Please fill in all required fields.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const normalizedEmail = form.email?.toLowerCase().trim();
    if (!normalizedEmail) {
      setError('Please enter a valid email.');
      return;
    }


    setLoading(true);

    try {
      await API.post('/auth/send-email-verification', {
        to: normalizedEmail,
        subject: 'Verify your email - Merchant Marvels',
        text: `Your verification code is:`, // backend appends code
      });

      setStep(2);
    } catch (err) {
      console.error('Email send error:', err);
      setError('Failed to send verification email.');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailCode = async () => {
    setError('');
    const res = await API.post('/auth/verify-email-code', {
      email: form.email.toLowerCase().trim(),
      code: emailCode
    });

    if (res.data.verified) {
      setStep(3);
      await sendPhoneVerification();
    } else {
      setError("Invalid email verification code.");
    }

  };

  const sendPhoneVerification = async () => {
    let phone = form.phone;
    if (!phone.startsWith('+1')) {
      phone = `+1${phone.replace(/\D/g, '').slice(0, 10)}`;
    }

    if (phone.length !== 12) {
      setError('Please enter a valid 10-digit US phone number.');
      return;
    }

    setForm((prev) => ({ ...prev, phone }));
    setLoading(true);
    setError('');

    try {
      await API.post('/auth/send-verification', { phone });
    } catch (err) {
      console.error('Phone verification error:', err);
      setError('Failed to send SMS verification.');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    const phone = form.phone;
    setLoading(true);
    setError('');

    try {
      const res = await API.post('/auth/verify-code', { phone, code: phoneCode });
      if (res.data.success) {
        await handleSubmit();
      } else {
        setError('Invalid phone verification code.');
      }
    } catch (err) {
      console.error('Phone code error:', err);
      setError('Phone verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const normalizedEmail = form.email?.toLowerCase().trim();
      const payload = { ...form, email: normalizedEmail };
      await API.post('/auth/register', payload);
      navigate('/login');
    } catch (err) {
      console.error('Register error:', err);
      const message =
        err.response?.data?.error || 'Registration failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep((prev) => Math.max(1, prev - 1));
    setError('');
  };

  return (
    <div className="mm-login-container">
      {/* Left hero */}
      <div className="mm-login-left">
        <div className="mm-welcome-message">
          <h1>Welcome To Merchant Marvels</h1>
          <p>
            Join our community of thousands and unlock exclusive insights daily
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="mm-login-right">
        <div className="mm-login-logo-wrapper">
          <img
            src={logo}
            alt="Merchant Marvels"
            className="mm-login-logo"
          />
        </div>

        <div className="mm-login-form-wrapper">
          <form
            className="mm-login-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <h2 className="mm-login-title">
              {step > 1 && (
                <span
                  onClick={goBack}
                  style={{ cursor: 'pointer', marginRight: 8 }}
                >
                  ←
                </span>
              )}
              Register
            </h2>

            {step === 1 && (
              <p className="mm-login-subtitle">
                Create your account to get started
              </p>
            )}

            {step > 1 && (
              <p className="mm-login-subtitle">Step {step} of 3</p>
            )}

            {error && <div className="mm-error-message">{error}</div>}

            {step === 1 && (
              <>
                <div className="mm-register-grid">
                  <div>
                    <div className="mm-label">Full Name</div>
                    <input
                      name="name"
                      className="mm-login-input"
                      placeholder="Enter Your Full Name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <div className="mm-label">Business Name</div>
                    <input
                      name="bname"
                      className="mm-login-input"
                      placeholder="Enter Your Business Name"
                      value={form.bname}
                      onChange={handleChange}
                      required
                    />
                  </div>



                  <div>
                    <div className="mm-label">Password</div>
                    <input
                      name="password"
                      type="password"
                      className="mm-login-input"
                      placeholder="Enter Password"
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <div className="mm-label">Confirm Password</div>
                    <input
                      name="confirmPassword"
                      type="password"
                      className="mm-login-input"
                      placeholder="Confirm Password"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="full-span">
                    <div className="mm-label">Email</div>
                    <input
                      name="email"
                      type="email"
                      className="mm-login-input"
                      placeholder="Enter Your Email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>


                  <div className="full-span">
                    <div className="mm-label">Address</div>
                    <input
                      name="address"
                      className="mm-login-input"
                      placeholder="Enter Your Address"
                      value={form.address}
                      onChange={handleChange}
                      required
                    />

                  </div>

                  <div className="full-span">
                    <div className="mm-label">Phone Number</div>
                    <input
                      name="phone"
                      className="mm-login-input"
                      placeholder="Enter Your Phone Number"
                      value={form.phone}
                      onFocus={() => {
                        if (!form.phone.startsWith('+1')) {
                          setForm({ ...form, phone: '+1' });
                        }
                      }}
                      onChange={(e) => {
                        let raw = e.target.value.replace(/\D/g, '');
                        if (raw.startsWith('1')) raw = raw.slice(1);
                        if (raw.length > 10) raw = raw.slice(0, 10);
                        setForm({ ...form, phone: `+1${raw}` });
                      }}
                      required
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="mm-login-button"
                  onClick={sendEmailVerification}
                  disabled={loading}
                >
                  {loading ? 'Sending Email...' : 'Next'}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <p className="mm-info-text">
                  We&apos;ve sent a verification code to{' '}
                  <strong>{form.email?.toLowerCase().trim()}</strong>.
                  Enter it below to verify your email.
                </p>
                <div className="mm-input-wrapper">
                  <input
                    className="mm-login-input"
                    placeholder="Enter Email Verification Code"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="mm-login-button"
                  onClick={verifyEmailCode}
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </button>
                <p className="mm-login-footer">
                  Didn&apos;t receive the code?{' '}
                  <button
                    type="button"
                    onClick={sendEmailVerification}
                    disabled={loading}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      margin: 0,
                      color: '#1f3b7b',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Resend
                  </button>
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <p className="mm-info-text">
                  We&apos;ve sent an SMS verification code to{' '}
                  <strong>{form.phone}</strong>. Enter it below to verify
                  your phone number.
                </p>
                <div className="mm-input-wrapper">
                  <input
                    className="mm-login-input"
                    placeholder="Enter SMS Code"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="mm-login-button"
                  onClick={verifyPhoneCode}
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Phone'}
                </button>
                <p className="mm-login-footer">
                  Didn&apos;t receive the code?{' '}
                  <button
                    type="button"
                    onClick={sendPhoneVerification}
                    disabled={loading}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      margin: 0,
                      color: '#1f3b7b',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Resend
                  </button>
                </p>
              </>
            )}

            <p className="mm-login-footer">
              Already Have An Account?{' '}
              <a href="/login" className="mm-link">
                Login
              </a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
